import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Metrotown = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.metrotown-burnaby:About Metrotown Paragraph">
          Metrotown, located in{' '}
          <LinkText href="/rental-listings/city/burnaby">Burnaby</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          , serves as the city’s central business district and is one of Metro
          Vancouver’s regional town centres. Situated to the east of Vancouver,
          Metrotown is renowned for its vibrant commercial landscape, notably
          housing Metropolis at Metrotown—the largest shopping centre in British
          Columbia. The area boasts a mix of high-rise residential and
          commercial buildings, offering residents a dynamic urban lifestyle.
          With excellent public transit access via the SkyTrain’s Expo Line,
          Metrotown seamlessly connects to downtown Vancouver and surrounding
          regions.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,830 - $2,230',
          bed2: '$2,346 - $2,746',
          bed3: '$2,946 - $3,346'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.metrotown-burnaby:Apartment Hunting Metrotown Paragraph 1">
            Finding an apartment for rent in Metrotown, Burnaby, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Metrotown lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.metrotown-burnaby:Apartment Hunting Metrotown Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Apartments for rent in Metrotown Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Metrotown, Burnaby
              </LinkText>
              , cater to a diverse demographic, including young professionals
              and families. The neighborhood offers a variety of high-rise and
              low-rise apartment complexes, many featuring modern amenities and
              proximity to shopping centers and public transit. The average rent
              for a one-bedroom apartment is approximately $2,295, while a
              two-bedroom unit averages around $3,071.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Houses for rent in Metrotown Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Metrotown, Burnaby
              </LinkText>
              , are ideal for families seeking more space and privacy. The area
              offers a selection of single-family homes and duplexes, often
              featuring multiple bedrooms and yards. The average monthly rent
              for houses is approximately $2,600, providing a more spacious
              alternative to apartment living while maintaining urban
              conveniences.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Townhouses for rent in Metrotown Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Metrotown, Burnaby
              </LinkText>{' '}
              , appeal to those seeking a balance between apartment living and
              single-family homes. These multi-level units often include modern
              designs, multiple bedrooms, and private outdoor spaces. Townhouses
              offer a comfortable living environment with the added benefit of
              proximity to urban amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Basements for rent in Metrotown Paragraph">
              Basements for rent in Metrotown, Burnaby, provide affordable
              housing options for students and young professionals. These units,
              often found in single-family homes, offer private entrances and
              essential amenities. Renting a basement suite allows tenants to
              enjoy the benefits of the neighborhood at a more accessible price
              point.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Condos for rent Metrotown Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Metrotown, Burnaby
              </LinkText>
              , are popular among professionals and small families seeking
              modern living spaces. Many condominiums in the area offer
              amenities such as fitness centers, communal lounges, and secure
              parking. The proximity to shopping centers and public transit
              enhances the appeal of condo living in Metrotown.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Rooms for rent in Metrotown Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Metrotown, Burnaby
              </LinkText>{' '}
              , are suitable for students and individuals seeking
              budget-friendly accommodations. These arrangements often involve
              shared common areas, providing an affordable living option without
              compromising on location. The neighborhood’s accessibility and
              amenities make it a convenient choice for room rentals.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.metrotown-burnaby:Short term rentals in Metrotown Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Metrotown, Burnaby
              </LinkText>
              , cater to tourists and business travelers requiring temporary
              accommodations. Options range from furnished apartments to private
              rooms, offering flexibility and comfort. The neighborhood’s
              shopping centers, dining options, and excellent transit
              connections make it an attractive choice for short-term stays.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.metrotown-burnaby:Exploring Metrotown Paragraph">
          Metrotown, situated in Burnaby, British Columbia, is a bustling urban
          center known for its commercial vibrancy and residential appeal. The
          neighborhood is anchored by{' '}
          <LinkText href="https://www.metropolisatmetrotown.com/">
            Metropolis at Metrotown
          </LinkText>
          , the largest shopping center in the province, offering a vast array
          of retail stores, dining options, and entertainment venues. Adjacent
          to the shopping hub is{' '}
          <LinkText href="https://www.burnaby.ca/explore-outdoors/parks/central-park">
            Central Park
          </LinkText>
          , a green oasis featuring walking trails, sports facilities, and
          tranquil ponds, providing residents and visitors with a natural
          retreat amidst the urban landscape. The area’s high-rise residential
          buildings offer panoramic views and modern living spaces, attracting a
          diverse community. With seamless connectivity to downtown Vancouver
          via the SkyTrain, Metrotown combines the conveniences of city living
          with suburban comforts.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.metrotown-burnaby:Things To Do Metrotown Paragraph">
          Metrotown offers a plethora of activities catering to various
          interests. Shopping enthusiasts can explore{' '}
          <LinkText href="https://www.metropolisatmetrotown.com/">
            Metropolis at Metrotown
          </LinkText>
          , boasting over 400 stores ranging from high-end fashion to specialty
          boutiques. For nature lovers,{' '}
          <LinkText href="https://www.burnaby.ca/explore-outdoors/parks/central-park">
            Central Park
          </LinkText>{' '}
          provides expansive green spaces, walking and jogging trails, and
          facilities like tennis courts and a pitch-and-putt golf course. Art
          aficionados can visit the{' '}
          <LinkText href="https://www.burnaby.ca/recreation-and-arts/arts-and-culture-facilities/burnaby-art-gallery">
            Burnaby Art Gallery
          </LinkText>
          , showcasing a diverse collection of visual arts.{' '}
          <LinkText href="https://www.burnaby.ca/recreation-and-arts/recreation-facilities/bonsor-recreation-complex">
            The Bonsor Recreation Complex
          </LinkText>{' '}
          offers fitness facilities, swimming pools, and community programs,
          ensuring recreational options for all ages. Additionally, the
          neighborhood hosts various cultural festivals and farmers’ markets
          throughout the year, enriching the community experience.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.metrotown-burnaby:Restaurants Metrotown Paragraph">
        <Paragraph>
          Metrotown Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.dolarshop.com/">The Dolar Shop</LinkText>{' '}
          - A premium hot pot destination, The Dolar Shop is famous for its
          Macau-style hot pot experience. Guests enjoy individualized soup
          bases, fresh ingredients, and impeccable service. Popular choices
          include Wagyu Cubes, Shrimp Paste, and their signature Exquisite
          Silver Soup. The sleek and modern ambiance makes it ideal for groups
          and special occasions.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.yelp.ca/biz/aroma-eatery-burnaby">
            Aroma Eatery
          </LinkText>{' '}
          - Located in Crystal Mall, Aroma Eatery serves delicious Taiwanese
          comfort food. Signature dishes like Braised Pork Chop with Rice and
          Salt & Pepper Chicken offer authentic flavors. The casual setting and
          affordable pricing make it a go-to spot for students and families.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.tripadvisor.ca/Restaurant_Review-g154943-d2552806-Reviews-Ipoh_Malay_Cuisine-Vancouver_British_Columbia.html">
            IPOH Malaysian Cuisine
          </LinkText>{' '}
          - This hidden gem in Crystal Mall’s food court is a local favorite for
          Malaysian street food. Must-try dishes include Chicken Laksa, IPOH Hor
          Fun, and Char Kway Teow. With generous portions and bold flavors, it’s
          a must-visit for those craving authentic Malaysian cuisine.
        </Paragraph>
        <Paragraph>
          4) <LinkText href="https://maruhachi.ca/">Maruhachi Ramen</LinkText> -
          One of the top ramen spots in Metrotown, Maruhachi Ramen is renowned
          for its rich, umami-packed broths and handmade noodles. Favorites
          include Tamago Ramen, Aka Ramen, and Spicy Chashu Ramen. The cozy
          setting makes it perfect for a comforting meal.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.metrotown-burnaby:Transportation Metrotown Paragraph">
          Getting around Metrotown, Burnaby, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Burnaby
          features an extensive network of bike lanes and rental services. Many
          parts of Metrotown are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Metrotown offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.metrotown-burnaby:Where To Stay Metrotown Paragraph">
          Accommodation options in Metrotown, Burnaby cater to those looking for
          a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Metrotown itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Metrotown
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Metrotown
